<!--
    NO `<p>` ANYWHERE IN HERE. `#global-modal-placeholder p` in the app's `styles.scss` centres every
    paragraph inside a modal and paints it gray-500 — an ID selector this file cannot override without
    `!important`. A `<div>` inherits none of it, which is why the copy below sits in divs and spans.

    Left-aligned throughout, and structured into three named sections rather than one block of prose:
    what is always fetched, what is already there, and what will be fetched. Those are three different
    facts about the same company, and a reader deciding what to pull needs them apart.
-->
<ham-modal
    [title]="'abcp.accountSurface.syncTitle' | i18next"
    size="md"
    [showCloseIcon]="true"
    [showCancelButton]="true"
    [cancelText]="'abcp.accountSurface.syncCancel' | i18next"
    [saveText]="'abcp.accountSurface.syncStart' | i18next"
    [disabled]="disabled"
    (cancel)="onClose()"
    (save)="onStart()"
>
    <div class="erp-sync">
        <div class="erp-sync__lead">{{ 'abcp.accountSurface.syncIntro' | i18next }}</div>

        <!-- ── Always fetched, and not a choice: the closing's own figures still change ── -->
        <section class="erp-sync__section" *ngIf="closingPeriodLabel !== null">
            <div class="erp-sync__heading">
                <span class="erp-sync__heading-text">{{ 'abcp.accountSurface.syncOwnHeading' | i18next }}</span>
            </div>
            <div class="erp-sync__card">
                <div class="erp-sync__period" data-testid="erp-sync-own">
                    <!-- One sentence: a period name beside its range read as two codes for the same year. -->
                    <span class="erp-sync__period-name">{{
                        'abcp.accountSurface.syncOwnPeriod' | i18next : { range: closingPeriodLabel.range }
                    }}</span>
                    <span class="erp-sync__tag erp-sync__tag--fetch">
                        {{ 'abcp.accountSurface.syncTagWillFetch' | i18next }}
                    </span>
                </div>
            </div>
        </section>

        <!-- ── Already stored. Listed rather than hidden: "why is 2024 not offered" is a question the
             dialog should answer before it is asked. ── -->
        <section class="erp-sync__section" *ngIf="held.length > 0">
            <div class="erp-sync__heading">
                <span class="erp-sync__heading-text">{{ 'abcp.accountSurface.syncHeldHeading' | i18next }}</span>
                <span class="erp-sync__heading-count">{{ held.length }}</span>
            </div>
            <div class="erp-sync__hint">{{ 'abcp.accountSurface.syncHeldHint' | i18next }}</div>
            <div class="erp-sync__card" data-testid="erp-sync-held">
                <div class="erp-sync__period" *ngFor="let period of held">
                    <span class="erp-sync__period-name">{{ period.name }}</span>
                    <span class="erp-sync__period-range">{{ period.range }}</span>
                    <span class="erp-sync__tag erp-sync__tag--held">
                        {{ 'abcp.accountSurface.syncTagHeld' | i18next }}
                    </span>
                </div>
            </div>
        </section>

        <!-- ── What will be fetched. Editable, because no server can enumerate Abacus' fiscal years. ── -->
        <section class="erp-sync__section">
            <div class="erp-sync__heading">
                <span class="erp-sync__heading-text">{{ 'abcp.accountSurface.syncFetchHeading' | i18next }}</span>
            </div>
            <div class="erp-sync__empty" *ngIf="rows.length === 0" data-testid="erp-sync-empty">
                {{ emptyMessageKey | i18next }}
            </div>

            <div class="erp-sync__rows" *ngIf="rows.length > 0">
                <!-- One header row instead of a label on every field: eight repeated words for four
                     periods is noise, and the picker omits its label when none is given. -->
                <div class="erp-sync__row erp-sync__row--head">
                    <span class="erp-sync__row-index"></span>
                    <span class="erp-sync__col-label">{{ 'abcp.accountSurface.syncFrom' | i18next }}</span>
                    <span class="erp-sync__col-label">{{ 'abcp.accountSurface.syncTo' | i18next }}</span>
                    <span class="erp-sync__row-action"></span>
                </div>

                <div
                    class="erp-sync__row"
                    *ngFor="let row of rows.controls; let i = index"
                    [attr.data-testid]="'erp-sync-row-' + i"
                >
                    <span class="erp-sync__row-index">{{ positionOf(i) }}</span>
                    <app-date-picker-flyout
                        [formControl]="row.controls.start"
                        flyoutClass="erp-sync__date-flyout"
                    ></app-date-picker-flyout>
                    <app-date-picker-flyout
                        [formControl]="row.controls.end"
                        flyoutClass="erp-sync__date-flyout"
                    ></app-date-picker-flyout>
                    <ham-button
                        class="erp-sync__remove"
                        [attr.data-testid]="'erp-sync-remove-' + i"
                        variant="transparent"
                        size="toolbar"
                        icon="assets/hamIcons/x.svg"
                        [hideText]="true"
                        [hamTooltip]="'abcp.accountSurface.syncRemovePeriod' | i18next"
                        tooltipClass="abcp"
                        (buttonClick)="removeRow(i)"
                    ></ham-button>
                </div>
            </div>

            <ham-button
                class="erp-sync__add"
                data-testid="erp-sync-add"
                *ngIf="maxRows > 0"
                variant="transparent"
                size="sm"
                icon="assets/hamIcons/plus-outline.svg"
                (buttonClick)="addRow()"
            >
                {{ 'abcp.accountSurface.syncAddPeriod' | i18next }}
            </ham-button>

            <div class="erp-sync__notice erp-sync__notice--error" *ngIf="limitReached" data-testid="erp-sync-limit">
                {{ 'abcp.accountSurface.syncMaxRowsReached' | i18next }}
            </div>
        </section>

        <!-- A started-and-unfinished row blocks the button. Saying so beats a button that looks broken. -->
        <div class="erp-sync__notice" *ngIf="hasIncompleteRow" data-testid="erp-sync-incomplete">
            {{ 'abcp.accountSurface.syncIncompleteRow' | i18next }}
        </div>

        <div class="erp-sync__notice erp-sync__notice--error" *ngIf="error !== null" data-testid="erp-sync-error">
            {{ error | i18next }}
        </div>
    </div>
</ham-modal>
